<script lang="ts" setup>
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  faCheckCircle,
  faChevronUp,
  faCircleExclamation,
  faCircleNotch,
  faCircleQuestion,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn, GpFadeTransition, useFaIcons } from '@gportal/uikit';
import useGlobalConfig from '@panel/composables/useGlobalConfig';
import LayoutOrderSummary from '@panel/layouts/order/Layout.Order.Summary.vue';
import { useWindowSize } from '@vueuse/core';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { type Authentication, useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { type Flavour, FlavourAvailability } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import { AuthenticationType, type BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import type { Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import type { CreateNodeResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  sectionsMapping: Record<
    string,
    {
      label: string;
      isVisible: boolean;
      isMounted: boolean;
      isLoading?: boolean;
      isDone?: boolean;
      isInvalid?: boolean;
    }
  >;
  order: {
    datacenter: Datacenter;
    flavour: Flavour;
    image: BasicImage;
    billingPeriod: BillingPeriod;
    authentication: Authentication;
    settings: {
      userData: string;
    };
    nodes: Array<string>;
  };
}>();

const { docs } = useGlobalConfig();

useFaIcons({ faCircle, faCheckCircle, faCircleExclamation, faChevronUp, faCircleNotch });

const isReady = computed(() => {
  if (
    !props.order.datacenter ||
    !props.order.authentication ||
    !props.order.flavour ||
    !props.order.image ||
    !props.order.nodes ||
    props.order.flavour.availability === FlavourAvailability.OUT_OF_STOCK ||
    props.order.nodes.length === 0
  ) {
    return false;
  }

  if (
    props.order.authentication.type === AuthenticationType.PASSWORD &&
    (!props.order.authentication.password || props.order.authentication.password.length === 0)
  ) {
    return false;
  }

  if (
    props.order.authentication.type === AuthenticationType.SSH &&
    props.order.authentication.sshKeys.length === 0
  ) {
    return false;
  }

  return true;
});

const router = useRouter();
const nodeStore = useNodeStore();
const projectStore = useProjectStore();
const { addNotification } = useNotifications();

const create = () => {
  if (isReady.value) {
    nodeStore
      .createNodes(props.order)
      .then((resp: CreateNodeResponse) => {
        addNotification({
          message: 'The nodes has been created.',
          type: 'success',
        });
        if (resp.nodes.length === 1) {
          router.push({
            name: 'ProjectNodeOverview',
            params: {
              id: resp.nodes[0].id,
              projectId: projectStore.project!.id,
            },
          });
        } else {
          router.push({
            name: 'ProjectNodes',
            params: { projectId: projectStore.project!.id },
          });
        }
      })
      .catch((error: ServiceError) => {
        addNotification({
          message: error.message,
          type: 'danger',
        });
      });
  }
};

const isExpanded = ref(false);
watch(
  () => props.order.nodes,
  (newValue) => {
    isExpanded.value = newValue.length > 0;
  },
);

const { width } = useWindowSize();
watchEffect(() => {
  if (width.value >= 1600 && props.order.nodes.length > 0) {
    isExpanded.value = true;
  }
});
</script>

<template>
  <LayoutOrderSummary
    :class="{
      'order:tw-max-h-80 tw-max-h-28': !isExpanded,
      'order:tw-max-h-[45rem] tw-max-h-[28rem] md:tw-max-h-[30rem]':
        isExpanded && order.billingPeriod === BillingPeriod.MONTHLY,
      'order:tw-max-h-[38rem] tw-max-h-[21rem]':
        isExpanded && order.billingPeriod === BillingPeriod.HOURLY,
    }"
    :sections-mapping="sectionsMapping"
    class="tw-transition-[max-height]"
    title="Order summary"
  >
    <template #header>
      <div class="tw-flex tw-items-center tw-justify-between">
        <p class="tw-text-2xl tw-font-bold">Order summary</p>
        <GpBtn
          v-if="order.nodes.length > 0"
          btn-type="transparent"
          class="order:tw-hidden tw-py-2"
          size="sm"
          @click="isExpanded = !isExpanded"
        >
          <FontAwesomeIcon :class="{ 'tw-rotate-180': isExpanded }" :icon="['fas', 'chevron-up']" />
        </GpBtn>
      </div>
    </template>

    <template #footer>
      <GpFadeTransition>
        <div v-if="isExpanded">
          <div class="order:tw-block tw-mt-2 tw-flex tw-w-full tw-items-end tw-justify-between">
            <div>
              <span class="tw-font-bold">Nodes</span>
              <p class="tw-mb-4">
                <span class="tw-font-mono"> {{ order.nodes.length }}x </span>
                Node<template v-if="order.nodes.length > 1">s</template>:&nbsp;
                <span class="tw-font-mono">
                  {{ order.flavour.name }}
                </span>
              </p>
              <p v-if="order.image.containsSplaLicense" class="tw-mb-4">
                + Windows license / month
                <a :href="`${docs.url}/guides/windows-licensing`" target="_blank">
                  <FontAwesomeIcon :icon="faCircleQuestion" class="tw-ml-1 tw-text-gp-rhino" />
                </a>
              </p>
              <!---<p class="tw-mb-4">
                <GpTag
                  v-if="order.billingPeriod === BillingPeriod.MONTHLY"
                  size="extra-small"
                  tag-type="neutral"
                  outline
                  class="tw-uppercase"
                >
                  Billing per month
                </GpTag>
                <GpTag
                  v-if="order.billingPeriod === BillingPeriod.HOURLY"
                  size="extra-small"
                  tag-type="neutral"
                  outline
                  class="tw-uppercase"
                >
                  Billing per hour
                </GpTag>
              </p>-->
            </div>
            <!-- <p v-if="monthlyPrice != null" class="tw-text-end">
              <span class="tw-text-2xl tw-font-bold tw-text-gp-cyan"> €{{ monthlyPrice }} </span>
              <br />
              <span class="tw-text-xs">
                <template v-if="order.billingPeriod === BillingPeriod.HOURLY">
                  estimated / month
                </template>
                <template v-if="order.billingPeriod === BillingPeriod.MONTHLY">
                  estimated / month
                </template>
                <template v-if="taxRate === 0"> (excl. VAT) </template>
                <template v-else> (incl. {{ taxRate }}% VAT) </template>
              </span>
            </p>-->
          </div>
          <GpBtn
            v-playwright="`order-summary-create-button`"
            :disabled="!isReady"
            class="tw-mt-4 tw-w-full"
            @click="create()"
          >
            Create node<template v-if="order.nodes.length > 1">s </template>
          </GpBtn>
          <p v-if="order.billingPeriod === BillingPeriod.MONTHLY" class="tw-pt-3 tw-text-sm">
            <strong class="tw-text-gp-orange">Note:</strong>
            Billing is due monthly, created nodes can't be deleted before one month. Nodes created
            will extend automatically until the delete has scheduled.
          </p>
        </div>
      </GpFadeTransition>
    </template>
  </LayoutOrderSummary>
</template>
